<!-- Main Content Area -->
<div class="scheduling-page">
  <div class="scheduling-page__container">
    <!-- Search Bar -->
    <div class="scheduling-page__search">
      <div class="scheduling-page__search-box">
        <mat-icon class="scheduling-page__search-icon">search</mat-icon>
        <input
          class="scheduling-page__search-input scheduling-page__search-input--desktop"
          type="text"
          placeholder="Search"
          [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)"
        />
        <input
          class="scheduling-page__search-input scheduling-page__search-input--mobile"
          type="text"
          placeholder="Search services..."
          [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)"
        />
      </div>
    </div>

    <!-- Content: Cards Grid + Cart -->
    <div class="scheduling-page__content">
      <div class="scheduling-page__main">
        @if (loading()) {
          @if (flowType() === 'service') {
            <!-- Skeleton loaders for group cards -->
            <div class="scheduling-page__grid">
              @for (i of [1, 2, 3, 4]; track i) {
                <div class="scheduling-page__skeleton-card">
                  <div class="scheduling-page__skeleton-circle"></div>
                  <div class="scheduling-page__skeleton-lines">
                    <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--long"></div>
                    <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--medium"></div>
                  </div>
                </div>
              }
            </div>
            <!-- Skeleton loaders for additional services -->
            <div class="scheduling-page__additional-services">
              <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--title"></div>
              @for (i of [1, 2]; track i) {
                <div class="scheduling-page__skeleton-event">
                  <div class="scheduling-page__skeleton-lines">
                    <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--long"></div>
                    <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--short"></div>
                  </div>
                  <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--duration"></div>
                  <div class="scheduling-page__skeleton-circle scheduling-page__skeleton-circle--small"></div>
                </div>
              }
            </div>
          } @else {
            <!-- Skeleton loaders for group/calendar event list -->
            <div class="scheduling-page__inline-skeleton">
              <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--title"></div>
              @for (i of [1, 2, 3, 4]; track i) {
                <div class="scheduling-page__skeleton-event">
                  <div class="scheduling-page__skeleton-lines">
                    <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--long"></div>
                    <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--short"></div>
                  </div>
                  <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--duration"></div>
                  <div class="scheduling-page__skeleton-circle scheduling-page__skeleton-circle--small"></div>
                </div>
              }
            </div>
          }
        } @else if (loadError()) {
          <app-booking-error
            title="Unable to load services"
            message="Something went wrong while loading the services. Please try again later."
          ></app-booking-error>
        } @else {
          <!-- GROUP / CALENDAR FLOW: inline event list -->
          @if (flowType() === 'group' || flowType() === 'calendar') {
            <!-- Group heading (group routes only) -->
            @if (groupName()) {
              <div class="scheduling-page__group-header">
                <h3 class="scheduling-page__group-title">{{ groupName() }}</h3>
                @if (groupDescription()) {
                  <p class="scheduling-page__group-description">{{ groupDescription() }}</p>
                }
              </div>
            }

            <div class="scheduling-page__events-list">
              @for (event of filteredEventTypes(); track event.id) {
                <div
                  class="scheduling-page__event-item"
                  [class.scheduling-page__event-item--selected]="isEventSelected(event)"
                  [class.scheduling-page__event-item--disabled]="isEventDisabled(event)"
                  [class.scheduling-page__event-item--unavailable]="isEventUnavailable(event)"
                  (click)="!isEventDisabled(event) && !isEventUnavailable(event) && onInlineEventToggle(event)"
                >
                  <div class="scheduling-page__event-info">
                    <span class="scheduling-page__event-name">
                      {{ event.name }}
                      @if (isEventUnavailable(event)) {
                        <glxy-badge color="yellow" size="small">Currently unavailable</glxy-badge>
                      }
                    </span>
                    @if (event.description) {
                      <span class="scheduling-page__event-description" [title]="event.description">{{ event.description }}</span>
                    }
                  </div>

                  <div class="scheduling-page__event-details">
                    <span class="scheduling-page__event-duration">{{ durationMinutes(event.duration) }}</span>
                  </div>

                  <div class="scheduling-page__event-action">
                    @if (isEventSelected(event)) {
                      <div class="scheduling-page__event-check scheduling-page__event-check--active">
                        <mat-icon>check</mat-icon>
                      </div>
                    } @else {
                      <div class="scheduling-page__event-check" [style.cursor]="isEventUnavailable(event) ? 'not-allowed' : ''">
                        <mat-icon>add</mat-icon>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>

            @if (filteredEventTypes().length === 0 && searchQuery()) {
              <p class="scheduling-page__no-results">No services found matching "{{ searchQuery() }}"</p>
            }
          }

          <!-- Empty state: no events loaded for any flow -->
          @if (flowType() !== 'service' && filteredEventTypes().length === 0 && !searchQuery()) {
            <div class="scheduling-page__error">
              <p>No services available at this time.</p>
            </div>
          }

          <!-- SERVICE FLOW: group cards grid + additional services -->
          @if (flowType() === 'service') {
            <!-- Group Cards Grid -->
            @if (filteredGroups().length > 0) {
              <div class="scheduling-page__grid">
                @for (group of filteredGroups(); track group.id) {
                  <app-booking-service-card
                    [title]="group.name"
                    [description]="group.description"
                    [initials]="getInitials(group.name)"
                    [badgeText]="getServiceCountBadge(group)"
                    (cardClick)="onGroupClick(group)"
                  ></app-booking-service-card>
                }
              </div>
            }

            @if (filteredGroups().length === 0 && filteredEventTypes().length === 0) {
              <div class="scheduling-page__error">
                <p>No services available at this time.</p>
              </div>
            }

            <!-- Additional Services (Single Events) -->
            @if (filteredEventTypes().length > 0) {
              <div class="scheduling-page__additional-services">
                <h3 class="scheduling-page__additional-services-title">Additional Services</h3>

                <div class="scheduling-page__events-list">
                  @for (event of filteredEventTypes(); track event.id) {
                    <div
                      class="scheduling-page__event-item"
                      [class.scheduling-page__event-item--selected]="isEventSelected(event)"
                      [class.scheduling-page__event-item--disabled]="isStandaloneEventDisabled(event)"
                      [class.scheduling-page__event-item--unavailable]="isEventUnavailable(event)"
                      (click)="!isStandaloneEventDisabled(event) && !isEventUnavailable(event) && onStandaloneEventToggle(event)"
                    >
                      <div class="scheduling-page__event-info">
                        <span class="scheduling-page__event-name">
                          {{ event.name }}
                          @if (isEventUnavailable(event)) {
                            <glxy-badge color="yellow" size="small">Currently unavailable</glxy-badge>
                          }
                        </span>
                        @if (event.description) {
                          <span class="scheduling-page__event-description" [title]="event.description">{{ event.description }}</span>
                        }
                      </div>

                      <div class="scheduling-page__event-details">
                        <span class="scheduling-page__event-duration">{{ durationMinutes(event.duration) }}</span>
                      </div>

                      <div class="scheduling-page__event-action">
                        @if (isEventSelected(event)) {
                          <div class="scheduling-page__event-check scheduling-page__event-check--active">
                            <mat-icon>check</mat-icon>
                          </div>
                        } @else {
                          <div class="scheduling-page__event-check" [style.cursor]="isEventUnavailable(event) ? 'not-allowed' : ''">
                            <mat-icon>add</mat-icon>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          }
        }
      </div>

      <!-- Cart Sidebar -->
      <div class="scheduling-page__cart-wrapper">
      <app-booking-cart
        [items]="cartItems()"
        [ctaLabel]="cartCtaLabel()"
        [ctaDisabled]="selectedEvents().length === 0"
        [warningMessage]="isMaxEventsReached() ? 'Max of 5 services can be selected' : ''"
        (ctaClick)="onChooseProvider()"
        (removeItem)="onRemoveCartItem($event)"
      ></app-booking-cart>
    </div>
    </div>
  </div>
</div>
